import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { authFetch } from "../../../api";

export default function TimelineSelector() {
  const userId = localStorage.getItem("userId");
  const navigate = useNavigate();
  const [selectedClass, setSelectedClass] = useState("");
  const [preference, setPreference] = useState("");
  const isDisabled = !selectedClass || !preference;
  const [streams, setStreams] = useState([]);
  const completedClass = Number(localStorage.getItem("completedClass") || 0);
  const [enrolledClass, setEnrolledClass] = useState(null);
  const [classOptions, setClassOptions] = useState([]);

  const fetchStream = async () => {
    try {
      const response = await authFetch("timeline/show-all-streams", {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });

      const result = await response.json();

      if (result.success) {
        setStreams(result.data);
      }
    } catch (err) {
      console.error("Error fetching streams:", err);
    }
  };

  const checkClass = async () => {
    try {
      const res = await authFetch("timeline/show-student-enrolled-class", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ user_id: userId }),
      });

      const result = await res.json();

      const data = result.data;

      setEnrolledClass(data.student_enrolled_for);

      //  Map API data to dropdown format
      const mappedClasses = data.show_class_ids.map((id, index) => ({
        value: id,
        label: data.show_class_names[index],
      }));

      setClassOptions(mappedClasses);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    checkClass();
    fetchStream();
  }, []);

  const handleAutomedatedClick = async () => {
    try {
      const response = await authFetch(
        "timeline/show-automated-data-by-enrollment",
        {
          method: "POST",
          body: JSON.stringify({
            student_enrolled_for: Number(selectedClass),
            stream: Number(preference),
            user_id: localStorage.getItem("userId"),
          }),
          headers: { "Content-Type": "application/json" },
        },
      );
    } catch (err) {
      console.error("Error fetching automated timeline:", err);
    }
  };

  // useEffect(() => {
  //   const completed = Number(localStorage.getItem("completedClass") || 0);
  //   const next = completed + 1;
  //   if (next <= 4) {
  //     setSelectedClass(String(next));
  //   }
  // }, []);

  // useEffect(() => {
  //   if (classOptions.length > 0) {
  //     setSelectedClass(String(classOptions[0].value));
  //   }
  // }, [classOptions]);

  return (
    <div className="font-inter min-h-screen font-inter bg-white relative overflow-hidden rounded-xl">
      {/* Decorative purple shape top left */}
      <div className="absolute top-0 left-0 w-80 h-80 bg-purple-200 rounded-full blur-3xl opacity-40 -translate-x-1/2 -translate-y-1/2"></div>

      {/* Decorative purple shape bottom right */}
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-200 rounded-full blur-3xl opacity-30 translate-x-1/3 translate-y-1/3"></div>

      {/* Main content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 py-20">
        {/* Header section */}
        <div className="text-center mb-16">
          <h1 className="text-3xl font-bold text-black mb-6">
            Choose How You Want to
            <br />
            Build Your Timeline
          </h1>
          <p className="text-xl text-[#313439] max-w-2xl mx-auto">
            Select an option based on how much control you want over your
            learning journey.
          </p>
        </div>

        {/* Dropdown section */}
        <div className="flex flex-col md:flex-row gap-6 max-w-3xl mx-auto mb-16">
          {/* Class Dropdown */}
          <div className="w-full">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Class
            </label>
            <select
              value={selectedClass}
              onChange={(e) => setSelectedClass(e.target.value)}
              required
              className="w-full h-12 px-4 rounded-lg border border-gray-300 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#572575]"
            >
              <option value="" disabled>
                Select class
              </option>

              {classOptions.map((cls) => (
                <option
                  key={cls.value}
                  value={cls.value}
                  disabled={completedClass >= cls.value} // optional logic
                >
                  {cls.label}
                </option>
              ))}
            </select>
          </div>

          {/* Preference Dropdown */}
          <div className="w-full">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Preference
            </label>
            <select
              value={preference}
              onChange={(e) => setPreference(e.target.value)}
              required
              className="w-full h-12 px-4 rounded-lg border border-gray-300 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#572575]"
            >
              <option value="" disabled>
                Select preferences
              </option>

              {streams.map((stream) => (
                <option key={stream.id} value={stream.id}>
                  {stream.stream_name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Cards container */}
        <div className="grid md:grid-cols-2 gap-12">
          {/* Automated Timeline Card */}
          <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
            {/* Card image - blue gradient with decorative elements */}
            <div className=" ">
              <img src="/timeline/automated.png" alt="automated" />
            </div>

            {/* Card content */}
            <div className="p-8">
              <h3 className="text-2xl font-bold text-black mb-4">
                Automated Timeline
              </h3>
              <p className="text-gray-700 mb-8  leading-relaxed">
                Get a ready-made learning roadmap automatically generated based
                on your personality quiz results.
              </p>
              <button
                disabled={isDisabled}
                onClick={() => {
                  handleAutomedatedClick();
                  navigate("/sap/automated", {
                    state: {
                      classID: selectedClass,
                      stream: preference,
                    },
                  });
                }}
                className={`w-half text-sm font-semibold py-3 px-6 rounded-lg transition-colors ${
                  isDisabled
                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                    : "bg-[#572575] text-white hover:bg-gray-800"
                }`}
              >
                Use Automated Timeline
              </button>
            </div>
          </div>

          {/* Customized Timeline Card */}
          <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
            {/* Card image - purple gradient with decorative elements */}
            <div className=" ">
              <img src="/timeline/customized.png" alt="customized" />
            </div>

            {/* Card content */}
            <div className="p-8">
              <h3 className="text-2xl font-bold text-black mb-4">
                Customized Timeline
              </h3>
              <p className="text-gray-700 mb-8 leading-relaxed">
                Manually select activities and build a personalized timeline
                that matches your interests and pace.
              </p>
              <button
                disabled={isDisabled}
                onClick={() =>
                  navigate("/sap/customized", {
                    state: {
                      classID: selectedClass,
                      stream: preference,
                    },
                  })
                }
                className={`w-half text-sm font-semibold py-3 px-6 rounded-lg transition-colors
    ${
      isDisabled
        ? "bg-gray-300 text-gray-500 cursor-not-allowed"
        : "bg-[#572575] text-white hover:bg-gray-800"
    }`}
              >
                Build My Own Timeline
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
